'use client';

import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
  content: string;
}

export default function NewsSection() {
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch news data
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch('https://ymfierp.yegnamicrofinance.com/api/news/featured');
        const data = await response.json();
        setNewsItems(data.data || []);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const handleReadMore = (news: NewsItem) => {
    setSelectedNews(news);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 3) % newsItems.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 3 + newsItems.length) % newsItems.length);
  };

  // Get visible items (3 at a time)
 const getVisibleItems = () => {
  if (newsItems.length <= 3) return newsItems;
  return Array.from({ length: 3 }, (_, i) => {
    const index = (currentIndex + i) % newsItems.length;
    return newsItems[index];
  });
};

  // Show loading state
  if (loading) {
    return (
      <section className="relative w-full overflow-hidden py-12 md:py-20 lg:py-24" style={{ backgroundColor: colors.white }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: `${colors.primary} transparent ${colors.primary} transparent` }}></div>
              <p className="mt-4 text-lg" style={{ color: colors.primary }}>Loading news...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show empty state
  if (newsItems.length === 0) {
    return (
      <section className="relative w-full overflow-hidden py-12 md:py-20 lg:py-24" style={{ backgroundColor: colors.white }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold sm:text-3xl md:text-4xl lg:text-5xl" style={{ color: colors.textDark }}>
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})` }}>
                Latest News & Events
              </span>
            </h2>
            <p className="mt-6 text-lg" style={{ color: colors.primary }}>No news available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  const visibleItems = getVisibleItems();
  const totalPages = Math.ceil(newsItems.length / 3);
  const currentPage = Math.floor(currentIndex / 3);

  return (
    <section 
      className="relative w-full overflow-hidden py-12 transition-all duration-300 md:py-20 lg:py-24"
      style={{ 
        backgroundColor: colors.white,
        boxShadow: `0px 0px 40px ${colors.primary}20`
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-8 text-center md:mb-12 lg:mb-16"
        >
          <h2 className="text-2xl font-bold sm:text-3xl md:text-4xl lg:text-5xl" style={{ color: colors.textDark }}>
            <span 
              className="bg-gradient-to-r bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})`
              }}
            >
              Latest News & Events
            </span>
          </h2>
          {/* <p className="mx-auto mt-3 max-w-2xl text-base sm:text-lg md:text-lg" style={{ color: colors.primary }}>
            Stay informed about our financial innovations, community impact, and industry developments
          </p> */}
        </motion.div>

        {/* Carousel Container with Side Arrows */}
        <div className="relative">
          {/* News Grid - 3 Cards */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
            <AnimatePresence mode="wait">
              {visibleItems.map((item, index) => (
                <motion.article
                  key={`${item.id}-${currentIndex}-${index}`}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  className="group overflow-hidden rounded-xl shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 w-full"
                  style={{ backgroundColor: colors.whiteOff }}
                >
                  {/* Image Container */}
                  <div className="relative h-48 w-full overflow-hidden sm:h-56 md:h-52 lg:h-48" style={{ backgroundColor: `${colors.primary}10` }}>
                    {item.image ? (
                      <Image
                        src={item.image}
                        alt={item.title}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full flex items-center justify-center';
                            fallback.style.backgroundColor = `${colors.primary}10`;
                            fallback.innerHTML = `<span style="color:${colors.primary};font-size:3rem;font-weight:bold;">📰</span>`;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-5xl" style={{ color: colors.primary }}>📰</span>
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div 
                      className="absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-medium text-white shadow-md"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {item.category || 'News'}
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="mb-1 text-xs font-medium sm:mb-2 sm:text-sm" style={{ color: colors.primary }}>
                      {item.date}
                    </div>
                    <h3 className="mb-2 text-lg font-bold line-clamp-2 sm:mb-3 sm:text-xl" style={{ color: colors.textDark }}>
                      {item.title}
                    </h3>
                    <p className="mb-3 text-sm line-clamp-2 sm:mb-4 sm:text-base" style={{ color: colors.textDark }}>
                      {item.excerpt}
                    </p>

                    <div className="mt-3 sm:mt-4">
                      <button
                        onClick={() => handleReadMore(item)}
                        className="inline-flex items-center text-sm font-medium transition-all duration-300 group-hover:translate-x-1 sm:text-base"
                        style={{ color: colors.primary }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = colors.primaryDark;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = colors.primary;
                        }}
                      >
                        Read More
                        <svg
                          className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1 sm:h-4 sm:w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>

          {/* Navigation Arrows - Left and Right */}
          {newsItems.length > 3 && (
            <>
              {/* Left Arrow */}
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-5 lg:-translate-x-6 z-10 rounded-full p-2 md:p-3 shadow-lg transition-all hover:scale-110 hover:shadow-xl focus:outline-none"
                style={{ 
                  backgroundColor: `${colors.primary}`,
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                }}
                aria-label="Previous news"
              >
                <svg className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Right Arrow */}
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-5 lg:translate-x-6 z-10 rounded-full p-2 md:p-3 shadow-lg transition-all hover:scale-110 hover:shadow-xl focus:outline-none"
                style={{ 
                  backgroundColor: `${colors.primary}`,
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                }}
                aria-label="Next news"
              >
                <svg className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Pagination Dots */}
        {newsItems.length > 3 && (
          <div className="flex justify-center gap-2 mt-6 md:mt-8">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index * 3)}
                className={`rounded-full transition-all duration-300 ${
                  index === currentPage
                    ? 'w-8 h-2.5 md:w-10 md:h-3'
                    : 'w-2 h-2 md:w-2.5 md:h-2.5 hover:scale-125'
                }`}
                style={{
                  backgroundColor: index === currentPage ? colors.primary : `${colors.primary}30`,
                }}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 text-center md:mt-10"
        >
          <Link
            href="/news"
            className="inline-flex items-center rounded-2xl px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 sm:px-8 sm:py-3 sm:text-base"
            style={{
              background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`
            }}
          >
            View All News
            <svg
              className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </motion.div>

        {/* Modal for showing full content */}
        {selectedNews && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm z-50 p-4 overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="rounded-2xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: colors.white }}
            >
              <div className="mb-4">
                <span className="text-xs font-medium sm:text-sm" style={{ color: colors.primary }}>
                  {selectedNews.date}
                </span>
                <h2 className="text-xl font-bold mt-1 sm:text-2xl lg:text-3xl" style={{ color: colors.textDark }}>
                  {selectedNews.title}
                </h2>
              </div>

              <div className="relative h-48 w-full overflow-hidden rounded-lg sm:h-64">
                {selectedNews.image ? (
                  <Image
                    src={selectedNews.image}
                    alt={selectedNews.title}
                    fill
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${colors.primary}10` }}>
                    <span className="text-4xl" style={{ color: colors.primary }}>📰</span>
                  </div>
                )}
              </div>

              <div
                className="prose prose-sm max-h-[40vh] overflow-y-auto mt-4 sm:prose-base"
                style={{ color: colors.textDark }}
                dangerouslySetInnerHTML={{ __html: selectedNews.content }}
              />
              
              <button
                onClick={() => setSelectedNews(null)}
                className="mt-4 text-white px-4 py-2 rounded-lg transition-all hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: colors.primary }}
              >
                Close
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}